/**
 * DatePickerHelpers — constants and helpers for CalEventDatePicker.
 * Adapted from components/AppBarComponents/calendarHelpers.js — selectable variant.
 */

export const DAY_NAMES = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export const MONTH_NAMES = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
];

/**
 * Returns day cells for a given year/month.
 * Monday-first grid — null values represent empty leading cells.
 * @param {number} year
 * @param {number} month - 0-based
 * @returns {Array<number|null>}
 */
export function buildDayCells(year, month) {
    const firstDay = new Date(year, month, 1).getDay();
    // Convert Sunday=0 to Monday-first offset
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
}

/**
 * Returns true if the given day matches today's date.
 * @param {number|null} day
 * @param {number} year
 * @param {number} month - 0-based
 * @param {Date} today
 * @returns {boolean}
 */
export function isToday(day, year, month, today) {
    return (
        day !== null &&
        today.getFullYear() === year &&
        today.getMonth() === month &&
        today.getDate() === day
    );
}

/**
 * Returns true if the given day matches the selected ISO date string.
 * @param {number|null} day
 * @param {number} year
 * @param {number} month - 0-based
 * @param {string} selectedDate - YYYY-MM-DD, or ''
 * @returns {boolean}
 */
export function isSelected(day, year, month, selectedDate) {
    if (day === null || !selectedDate) return false;
    return selectedDate === toISODate(year, month, day);
}

/**
 * Formats year/month/day to ISO date string YYYY-MM-DD.
 * @param {number} year
 * @param {number} month - 0-based
 * @param {number} day
 * @returns {string}
 */
export function toISODate(year, month, day) {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
}

/**
 * Parses an ISO date string (YYYY-MM-DD) to { year, month, day }.
 * Returns null if the string is empty or invalid.
 * @param {string} isoDate
 * @returns {{ year: number, month: number, day: number }|null}
 */
export function parseISODate(isoDate) {
    if (!isoDate) return null;
    const [y, m, d] = isoDate.split('-').map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month: m - 1, day: d };
}
